import { initSwiper } from '../components/swiper';
import { CLASSES, OKENDO } from '../utils/constants';

/** Escape HTML entities to prevent XSS from third-party API content */
const escapeHTML = (str) => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

/** DOM selectors for the Okendo Reviews section */
const SELECTORS = {
  SECTION: '[data-okendo-reviews]',
  LIST: '[data-okendo-reviews-list]',
  SWIPER: '[data-okendo-swiper]',
  CONTROLS: '[data-okendo-reviews-controls]',
  VIEW_ALL_CONTAINER: '[data-okendo-reviews-view-all-container]',
  VIEW_ALL: '[data-okendo-reviews-view-all]',
  GRID_CONTAINER: '[data-okendo-reviews-grid-container]',
  GRID: '[data-okendo-reviews-grid]',
  LOAD_MORE_BTN: '[data-okendo-reviews-load-more]',
  //LOADING: '[data-okendo-reviews-loading]'
};

const ERROR = 'Okendo reviews fetch error:';

const STAR_SVG_FILLED = `<svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M8 0L9.79611 5.52786H15.6085L10.9062 8.94427L12.7023 14.4721L8 11.0557L3.29772 14.4721L5.09383 8.94427L0.391548 5.52786H6.20389L8 0Z" fill="#CCBD9F"/>
</svg>`;

const STAR_SVG_EMPTY = `<svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M8 0L9.79611 5.52786H15.6085L10.9062 8.94427L12.7023 14.4721L8 11.0557L3.29772 14.4721L5.09383 8.94427L0.391548 5.52786H6.20389L8 0Z" fill="#DADEE2"/>
</svg>`;

/** Render a row of star SVGs for a given rating (clamped 0–5) */
const renderStars = (rating) => {
  const clamped = Math.max(0, Math.min(OKENDO.MAX_RATING, Math.round(rating)));
  let stars = '';
  for (let i = 0; i < OKENDO.MAX_RATING; i += 1) {
    stars += i < clamped ? STAR_SVG_FILLED : STAR_SVG_EMPTY;
  }
  return stars;
};

/** Build HTML for a single review slide */
const renderReviewCard = (review) => {
  const { rating, title, body, reviewer } = review;
  const name = reviewer?.displayName || 'Anonymous';

  return `
    <li class="swiper-slide">
      <div class="flex flex-col gap-4 w-full h-full bg-white p-5">
        <div class="flex gap-1.5 items-center">
          <div class="flex gap-0.5">${renderStars(rating)}</div>
          <span class="text-dark-grey text-xs">${escapeHTML(name)}</span>
        </div>
        ${title ? `<h3 class="heading-6">${escapeHTML(title)}</h3>` : ''}
        <div class="text-sm">${body ? escapeHTML(body) : ''}</div>
      </div>
    </li>
  `;
};

const renderGridCard = (review) => {
  const { rating, title, body, reviewer } = review;
  const name = reviewer?.displayName || 'Anonymous';

  return `
    <div>
      <div class="flex flex-col gap-4 w-full h-full bg-white p-5">
        <div class="flex gap-1.5 items-center">
          <div class="flex gap-0.5">${renderStars(rating)}</div>
          <span class="text-dark-grey text-xs">${escapeHTML(name)}</span>
        </div>
        ${title ? `<h3 class="heading-6">${escapeHTML(title)}</h3>` : ''}
        <div class="text-sm">${body ? escapeHTML(body) : ''}</div>
      </div>
    </div>
  `;
};

/** Bind smooth-scroll behaviour for any "scroll to reviews" links */
const initScrollToReviews = () => {
  document.querySelectorAll('[data-scroll-to-reviews]').forEach((link) => {
    link.addEventListener('click', (e) => {
      const target = document.querySelector('[data-oke-widget]') || document.getElementById('reviews');
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });
};

const loadMoreReviews = async (section) => {
  const grid = section.querySelector(SELECTORS.GRID);
  const loadMoreBtn = section.querySelector(SELECTORS.LOAD_MORE_BTN);
  const loadingSpinner = loadMoreBtn.querySelector('[data-loading-spinner]');
  const nextUrl = loadMoreBtn.dataset.href;


  let loading = false;

  const setLoading = (isLoading) => {
    loading = isLoading;
    loadMoreBtn?.classList.toggle('loading', isLoading);
    loadingSpinner?.classList.toggle('hidden', !isLoading);
  };

  setLoading(true);

  try {
    const response = await fetch(`${nextUrl}`);

    if (!response.ok) return;

    const data = await response.json();
    const reviews = data.reviews || [];

    //Hide the load more button if there are no additional reviews ie. a blank nextUrl
    if (data.nextUrl == '') {
      loadMoreBtn.classList.add(CLASSES.HIDDEN);
      loadMoreBtn.dataset.href = '';
    } else {
      loadMoreBtn.dataset.href = `${OKENDO.API_BASE}${data.nextUrl}`;
    }

    //Update grid with additonal review fetched via load more
    reviews.forEach((review) => {
      grid.insertAdjacentHTML('beforeend', renderGridCard(review));
    });
  } catch (err) {
    console.error(ERROR, err);
  } finally {
    setLoading(false);
  }
};

const initLoadMore = (section) => {
  const loadMoreBtn = section.querySelector(SELECTORS.LOAD_MORE_BTN);
  loadMoreBtn.addEventListener('click', (event) => {
    event.preventDefault();
    loadMoreReviews(section);
  });
};


const loadReviewsGrid = async (section, data) => {
  const grid = section.querySelector(SELECTORS.GRID);
  const loadMoreBtn = section.querySelector(SELECTORS.LOAD_MORE_BTN);
  const viewAllBtn = section.querySelector(SELECTORS.VIEW_ALL);
  const loadingSpinner = viewAllBtn.querySelector('[data-loading-spinner]');

  const reviews = data.reviews || [];

  //Hide the section entirely if there are no reviews
  if (!reviews.length) {
    section.classList.add(CLASSES.HIDDEN);
    return;
  }
  //Unhide the load more button if there are additional reviews ie. a nextURl
  if (data.nextUrl != '') {
    loadMoreBtn.classList.remove(CLASSES.HIDDEN);
    loadMoreBtn.dataset.href = `${OKENDO.API_BASE}${data.nextUrl}`;
    initLoadMore(section);
  }

  //Hide swiper, controls and view all 
  section.querySelector(SELECTORS.SWIPER).classList.add('hidden!');
  section.querySelector(SELECTORS.CONTROLS).classList.add(CLASSES.HIDDEN);
  section.querySelector(SELECTORS.VIEW_ALL_CONTAINER).classList.add(CLASSES.HIDDEN);
  //Reveal grid container
  section.querySelector(SELECTORS.GRID_CONTAINER).classList.remove(CLASSES.HIDDEN);
  grid.innerHTML = reviews.map(renderGridCard).join('');
  grid.classList.remove(CLASSES.HIDDEN);

};

const initViewAll = (section,data) => {
  const viewAllBtn = section.querySelector(SELECTORS.VIEW_ALL);
  const viewAllContainer = section.querySelector(SELECTORS.VIEW_ALL_CONTAINER);
  viewAllContainer.classList.remove(CLASSES.HIDDEN);
  viewAllBtn.addEventListener('click', () => {
    loadReviewsGrid(section,data);
  });
};

/**
 * Fetch and render reviews for a single section.
 * Separated from the loop so errors in one section don't block others.
 */
const initSection = async (section) => {
  const subscriberId = section.dataset.okendoSubscriberId;
  const productId = section.dataset.productId;
  const limit = section.dataset.reviewLimit || OKENDO.DEFAULT_LIMIT;
  const orderBy = section.dataset.reviewOrder || 'date desc';
  const list = section.querySelector(SELECTORS.LIST);
  const swiperEl = section.querySelector(SELECTORS.SWIPER);

  if (!subscriberId || !list) return;

  // Build the API endpoint — product-specific if an ID is set, otherwise store-wide
  const endpoint = productId
    ? `${OKENDO.API_BASE}/stores/${subscriberId}/products/shopify-${productId}/reviews`
    : `${OKENDO.API_BASE}/stores/${subscriberId}/reviews`;

  const params = new URLSearchParams({ limit, orderBy });

  try {
    const res = await fetch(`${endpoint}?${params}`);

    if (!res.ok) return;

    const data = await res.json();
    const reviews = data.reviews || [];

    // Hide the section entirely if there are no reviews
    if (!reviews.length) {
      section.classList.add(CLASSES.HIDDEN);
      return;
    }

    list.innerHTML = reviews.map(renderReviewCard).join('');

    initViewAll(section,data);

    if (swiperEl) {
      initSwiper(swiperEl);
    }
  } catch (err) {
    console.error(ERROR, err);
  }
};

/**
 * Initialise all Okendo Reviews sections.
 * Fetches reviews from the Okendo API, renders cards, and initialises Swiper.
 */
const initOkendoReviews = () => {
  initScrollToReviews();

  const sections = document.querySelectorAll(SELECTORS.SECTION);
  if (!sections.length) return;

  // Each section fetches independently — no need to await sequentially
  sections.forEach((section) => initSection(section));
};

export default initOkendoReviews;
